import React from "react";
import styled from "@emotion/styled";
import { ThemeContext, UserContext, LanguageContext } from "../contexts/Context";
import { lightTint, readableText, actionTextColor } from "../utils/themeContrast";
import { getAssetUrl, getCompanyAddress } from "./customerComponents/loginDisplayUtil";

export default function StandardCheckin({ hasMember, canGuest, loginBusy = false, onGuest, onMember, onSignUp, memberLabel, onReview, phone, password, setPhone, setPassword, onLanguage, onMenu }) {
  const theme = React.useContext(ThemeContext);
  const user = React.useContext(UserContext);
  const lang = React.useContext(LanguageContext)[user.lanIndex];
  const company = user.company || {};
  const requiresPassword = user.hasMember === true && !user.hideOptionalField;
  const business = company.shortName || company.companyName;
  const address = getCompanyAddress(company);
  const orderHeading = user.isInRoom ? lang.roomService
    : user.isTakeAway
      ? (user.includeDineIn || user.deliveryOrNot ? lang.startYourOrder : lang.takeAway)
      : lang.dineInOrder;
  return <Page themeContext={theme}>
    <Brand themeContext={theme}><div><strong>{business}</strong></div>{user.tableLabel && <Table themeContext={theme}>{lang.table} <b>{user.tableLabel}</b></Table>}</Brand>
    <Hero themeContext={theme} landingImage={getAssetUrl(user.landingImg)}>
      {company.companyLogo && <LogoFrame><img src={getAssetUrl(company.companyLogo)} alt={business || ""} /></LogoFrame>}
      <h1>{lang.welcome} {business}</h1>{user.greetingMessage && <p>{user.greetingMessage}</p>}</Hero>
    <Card themeContext={theme}>
      <FormHeading><h2>{orderHeading}</h2>{user.tableLabel && <Table themeContext={theme}>{lang.table} <b>{user.tableLabel}</b></Table>}</FormHeading>
      {address && <Address>{address}</Address>}
      {user.supportMultiLan && <Languages><button type="button" onClick={() => onLanguage(0)} aria-pressed={user.lanIndex == 0}>English</button><button type="button" onClick={() => onLanguage(1)} aria-pressed={user.lanIndex == 1}>{"\u4e2d\u6587"}</button></Languages>}
      {user.menus && user.menus.length > 1 && <Field>{lang.pleaseSelect}<select value={user.title || ""} onChange={e => onMenu(e.target.value)}><option value="" disabled>{lang.pleaseSelect}</option>{user.menus.map(menu => <option key={menu} value={menu}>{menu}</option>)}</select></Field>}
      {hasMember ? <form onSubmit={e => { e.preventDefault(); if (!loginBusy) onMember(); }}>
        <Field>{lang.phone}<input type="tel" autoComplete="tel" value={phone} onChange={e => setPhone(e.target.value)} placeholder={lang.phone} required /></Field>
        {requiresPassword && <Field>{lang.memberPassword}<input type="password" autoComplete="current-password" value={password} onChange={e => setPassword(e.target.value)} placeholder={lang.memberPassword} required /></Field>}
        <Primary themeContext={theme} type="submit" disabled={loginBusy} aria-busy={loginBusy}>{memberLabel || lang.memberSignIn}</Primary>
        {onSignUp && <Secondary themeContext={theme} type="button" disabled={loginBusy} onClick={onSignUp}>{lang.register}</Secondary>}
        {canGuest && <Guest themeContext={theme} type="button" disabled={loginBusy} onClick={onGuest}>{lang.continueAsGuest}</Guest>}
      </form> : canGuest && <Primary themeContext={theme} type="button" onClick={onGuest}>{lang.continueCheckin}</Primary>}
    <Review themeContext={theme} type="button" onClick={onReview}>{user.showKitchenStatus ? lang.reviewOrderStatus : lang.reviewOrder}</Review>
    </Card>
  </Page>;
}
const Page = styled.div`
  min-height: 100dvh; width: 100%; background: ${p => lightTint(p.themeContext.primaryColor,.05)};
  color: ${p => readableText(lightTint(p.themeContext.primaryColor,.05),p.themeContext.fontPrimary)};
  font-family: ${p => p.themeContext.fontFamily}; padding-bottom: 28px;
  @media(min-width:768px) {
    display: grid; grid-template-columns: minmax(0, 40fr) minmax(0, 60fr);
    grid-template-rows: auto 1fr; min-height: 100dvh; padding-bottom: 0;
  }
`;
const Brand = styled.header`
  padding: 14px 22px; display:flex; align-items:center; justify-content:space-between; gap:16px;
  > div { display:flex; align-items:center; gap:12px; } strong { font-size:16px; } img { width:46px; height:46px; object-fit:contain; border-radius:10px; }
  @media(min-width:768px) {
    grid-column:1; grid-row:1; padding:clamp(28px, 3vw, 60px);
    background: ${p => p.themeContext.primaryColor}; color: ${p => actionTextColor(p.themeContext)};
    > span { display:none; } > div { gap:16px; } strong { font-size:14px; font-weight:500; letter-spacing:.04em; }
    img { width:clamp(56px, 4.2vw, 80px); height:clamp(56px, 4.2vw, 80px); border:2px solid rgba(255,255,255,.8); border-radius:16px; }
  }
`;
const Table = styled.span`
  flex-shrink:0; background: #fff; color:#333; border:1px solid #ddd; border-radius:12px; padding:9px 12px; font-size:11px;
  b { color: ${p => readableText('#fff',p.themeContext.primaryColor)}; }
`;
const Hero = styled.div`
  background-color: ${p=>p.themeContext.primaryColor}; color:${p=>p.landingImage ? '#fff' : actionTextColor(p.themeContext)};
  position:relative;
  background-image:${p=>p.landingImage
    ? `linear-gradient(rgba(0,0,0,.55), rgba(0,0,0,.55)), url(${JSON.stringify(p.landingImage)})`
    : 'radial-gradient(ellipse at 50% 28%, rgba(255,255,255,.13), transparent 65%)'};
  background-size:cover; background-position:center; background-repeat:no-repeat;
  text-align:center; padding:24px 24px 110px;
  h1 { font-size:27px; line-height:1.2; font-weight:700; margin:0 auto; max-width:440px; }
  p { margin:10px 0 0; font-size:14px; font-weight:600; }
  @media(min-width:768px) {
    grid-column:1; grid-row:2; display:flex; flex-direction:column; align-items:center; justify-content:center;
    text-align:center; padding:16px clamp(28px, 3vw, 60px) clamp(48px, 10vh, 110px);
    ${p => !p.landingImage && 'background-image:radial-gradient(ellipse at 50% 35%, rgba(255,255,255,.14), transparent 65%);'}
    h1 { font-size:clamp(28px, 2.3vw, 44px); max-width:460px; margin:0; text-wrap:balance; }
    p { font-size:clamp(15px, 1.1vw, 19px); max-width:400px; line-height:1.5; margin-top:14px; }
  }
`;
const LogoFrame = styled.div`
  width:120px; height:120px; padding:12px; box-sizing:border-box; margin:0 auto 22px;
  display:grid; place-items:center; flex-shrink:0; background:#fff; border-radius:26px;
  box-shadow:0 14px 34px rgba(0,0,0,.16), 0 0 0 7px rgba(255,255,255,.1);
  img { display:block; width:100%; height:100%; min-height:0; object-fit:contain; border-radius:14px; }
  @media(min-width:768px) {
    width:clamp(180px, 17vw, 280px); height:clamp(180px, 17vw, 280px);
    padding:22px; border-radius:36px; margin-bottom:36px;
    box-shadow:0 22px 54px rgba(0,0,0,.18), 0 0 0 12px rgba(255,255,255,.09);
    img { border-radius:20px; }
  }
`;
const Card = styled.section`
  position:relative; margin:-90px 14px 0; padding:26px 18px 18px; border-radius:24px;
  background:${p=>p.themeContext.backgroundColor || '#fff'}; color:${p=>readableText(p.themeContext.backgroundColor,p.themeContext.fontPrimary)};
  box-shadow:0 5px 14px rgba(0,0,0,.12);
  h2 { margin:0 0 8px; font-size:23px; } form { margin:0; }
  @media(min-width:768px) {
    grid-column:2; grid-row:1 / 3; align-self:center; justify-self:center;
    width:calc(100% - 80px); max-width:500px; margin:48px 0; padding:0;
    border-radius:0; background:transparent; box-shadow:none;
    color: ${p => readableText(lightTint(p.themeContext.primaryColor,.05),p.themeContext.fontPrimary)};
    h2 { font-size:clamp(26px, 2vw, 36px); }
  }
`;
const FormHeading = styled.div`
  > span { display:none; }
  @media(min-width:768px) { display:flex; justify-content:space-between; align-items:center; gap:16px; margin-bottom:12px; > span { display:block; } h2 { margin:0; } }
`;
const Address = styled.p`font-size:13px; line-height:1.5; margin:0 0 20px; opacity:.8; @media(min-width:768px) { font-size:17px; margin-bottom:30px; }`;
const Field = styled.label`
  @media(min-width:768px) { && { font-size:16px; margin:28px 0; } && input, && select { min-height:60px; font-size:17px; padding:16px 20px; border-radius:16px; } }
  display:block; font-size:12px; margin:20px 0;
  input, select { display:block; width:100%; box-sizing:border-box; margin-top:6px; padding:14px; border:1px solid rgba(128,128,128,.2); border-radius:13px; background:rgba(128,128,128,.07); color:inherit; font:inherit; font-size:14px; min-height:46px; }
`;
const Primary = styled.button`
  &:disabled { opacity: .65; cursor: wait; }
  @media(min-width:768px) { && { min-height:64px; font-size:18px; box-shadow:0 6px 18px rgba(0,0,0,.12); } }
  width:100%; min-height:50px; border:0; border-radius:999px; padding:12px 18px; margin-top:4px;
  background:${p=>p.themeContext.primaryColor}; color:${p=>actionTextColor(p.themeContext)}; font:inherit; font-size:14px; font-weight:600; cursor:pointer;
`;
const Secondary = styled(Primary)`
  margin-top:12px; background:transparent; border:1.5px solid ${p=>p.themeContext.primaryColor};
  color:${p=>readableText(p.themeContext.backgroundColor,p.themeContext.primaryColor)};
  @media(min-width:768px) { && { box-shadow:none; } }
`;
const Guest = styled.button`
  @media(min-width:768px) { && { font-size:16px; margin-top:20px; } }
  display:block; margin:16px auto 0; border:0; padding:4px; background:transparent; font:inherit; font-size:13px; text-decoration:underline; cursor:pointer;
  color:${p=>readableText(p.themeContext.backgroundColor,p.themeContext.primaryColor)};
`;
const Review = styled(Guest)`margin:24px auto 0;`;
const Languages = styled.div`
  display:flex; gap:12px; margin:14px 0; button { background:transparent; color:inherit; border:0; padding:4px; cursor:pointer; } button[aria-pressed="true"] { font-weight:700; text-decoration:underline; }
`;
